import type { Result } from 'ts-results';
import { Err, Ok } from 'ts-results';

export type { Result, Ok, Err };

import type {
  ApplicationResource,
  EnvironmentResource,
  MemberResource,
  ProjectResource,
  PropertySelectors,
  ResourceSpecifier,
  ResourceType,
} from './ast';
import {
  account,
  agentGraph,
  agentOptimization,
  agentSkill,
  aiCodeSync,
  aiConfig,
  aiDataset,
  aiEvaluation,
  aiModelConfig,
  aiTool,
  promptSnippet,
  alert,
  application,
  applicationVersion,
  assignmentDataSource,
  attributeDataSource,
  contextKind,
  destination,
  domainVerification,
  environment,
  environmentReleasePlan,
  error,
  experiment,
  flag,
  goal,
  holdout,
  integration,
  isResourceType,
  layer,
  log,
  member,
  metric,
  metricDataSource,
  metricGroup,
  multiArmedBandit,
  notificationSubscription,
  observabilityDashboard,
  observabilitySettings,
  payloadFilter,
  pendingRequest,
  productAnalyticsDashboard,
  project,
  relayProxyConfig,
  releasePipeline,
  releasePolicy,
  repository,
  role,
  sdkKey,
  segment,
  serviceToken,
  // Observability resources
  session,
  team,
  template,
  token,
  trace,
  user,
  vega,
  view,
  webhook,
  ipAllowlist,
} from './ast';
import { ROLE_ATTRIBUTE_REGEX } from './constants';
import { Lexer } from './internal/lexer';
import type { Token } from './internal/token';

export class ParserError extends Error {
  name = 'ParserError';
  token?: Token;

  constructor(message: string, _token?: Token) {
    super(message);
    this.message = message;
    this.token = _token;
  }
}

export class SyntaxError extends ParserError {
  name = 'SyntaxError';
}

export class Parser {
  private readonly lexer: Lexer;

  // @ts-expect-error initialized via this.nextToken in constructor
  private currentToken: Token;
  // @ts-expect-error initialized via this.nextToken in constructor
  private peekToken: Token;

  constructor(lexer: Lexer) {
    this.lexer = lexer;

    // Initialize the current and peek tokens
    this.nextToken();
    this.nextToken();
  }

  public parse(): Result<ResourceSpecifier, ParserError> {
    if (this.currentToken.type === 'eof') {
      return Err(new ParserError('Empty input', copy(this.currentToken)));
    }

    if (this.currentToken.type !== 'resource-type') {
      return Err(new ParserError('Expected resource type'));
    }

    const result = this.parseResourceIdentifier();
    if (result.err) {
      return result;
    }

    return Ok(result.val);
  }

  private parseResourceIdentifier(): Result<ResourceSpecifier, ParserError> {
    const parts = this.readResourceIdentifierParts();
    if (parts.err) {
      switch (this.currentToken.text) {
        case 'acct':
          return Ok(account());
        case 'ip-allowlist':
          return Ok(ipAllowlist());
        default:
          return parts;
      }
    }

    const { type, name, tags, roleAttribute, tagsRoleAttribute } = parts.val;

    let specifier: ResourceSpecifier;

    switch (type) {
      case 'integration':
        specifier = integration(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'application':
        specifier = application(name, tags, roleAttribute, tagsRoleAttribute);
        if (this.isScopedResource()) {
          return this.parseApplicationScopedResourceSpecifier(specifier);
        }

        break;
      case 'domain-verification':
        specifier = domainVerification(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'member':
        specifier = member(name, tags, roleAttribute, tagsRoleAttribute);
        if (this.isScopedResource()) {
          return this.parseMemberScopedResourceSpecifier(specifier);
        }

        break;
      case 'proj':
        specifier = project(name, tags, roleAttribute, tagsRoleAttribute);
        if (this.isScopedResource()) {
          return this.parseProjectScopedResourceSpecifier(specifier);
        }

        break;
      case 'relay-proxy-config':
        specifier = relayProxyConfig(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'role':
        specifier = role(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'service-token':
        specifier = serviceToken(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'team':
        specifier = team(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'template':
        specifier = template(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'webhook':
        specifier = webhook(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'code-reference-repository':
        specifier = repository(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      case 'pending-request':
        specifier = pendingRequest(name, tags, roleAttribute, tagsRoleAttribute);
        break;
      default:
        return Err(new ParserError('oops'));
    }

    return Ok(specifier);
  }

  private parseProjectScopedResourceSpecifier(_project: ProjectResource): Result<ResourceSpecifier, ParserError> {
    const parts = this.readResourceIdentifierParts();
    if (parts.err) {
      return parts;
    }

    const { type, name, tags, views, propertySelectors, roleAttribute, tagsRoleAttribute, viewsRoleAttribute } =
      parts.val;

    switch (type) {
      case 'aiconfig':
        // Temporary: Fail safe if resourceId is an aiconfig without an environment
        // ie. "proj/default:aiconfig/my-config" -> "proj/default:env/*:aiconfig/my-config"
        const wildcardEnv = environment(
          _project,
          '*',
          tags,
          propertySelectors.critical,
          roleAttribute,
          tagsRoleAttribute,
        );
        return Ok(aiConfig(wildcardEnv, name, tags, views, roleAttribute, tagsRoleAttribute, viewsRoleAttribute));

      case 'env':
        const env = environment(_project, name, tags, propertySelectors.critical, roleAttribute, tagsRoleAttribute);
        if (this.isScopedResource()) {
          return this.parseEnvironmentScopedResourceSpecifier(env);
        }

        return Ok(env);
      case 'ai-model-config':
        return Ok(aiModelConfig(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'ai-dataset':
        return Ok(aiDataset(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'ai-tool':
        return Ok(aiTool(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'prompt-snippet':
        return Ok(promptSnippet(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'ai-evaluation':
        return Ok(aiEvaluation(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'payload-filter':
        return Ok(payloadFilter(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'metric':
        return Ok(metric(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'metric-group':
        return Ok(metricGroup(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'goal':
        return Ok(goal(_project, name, tags, roleAttribute));
      case 'release-pipeline':
        return Ok(releasePipeline(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'release-policy':
        return Ok(releasePolicy(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'context-kind':
        return Ok(contextKind(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'layer':
        return Ok(layer(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'view':
        return Ok(view(_project, name, tags, roleAttribute, tagsRoleAttribute, propertySelectors.affectsSdkPayload));
      case 'alert':
        return Ok(alert(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'observability-settings':
        return Ok(observabilitySettings(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'observability-dashboard':
        return Ok(observabilityDashboard(_project, name, tags, roleAttribute, tagsRoleAttribute));
      // Observability resources
      case 'session':
        return Ok(session(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'error':
        return Ok(error(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'log':
        return Ok(log(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'trace':
        return Ok(trace(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'vega':
        return Ok(vega(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'agent-graph':
        return Ok(agentGraph(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'agent-optimization':
        return Ok(agentOptimization(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'agent-skill':
        return Ok(agentSkill(_project, name, tags, roleAttribute, tagsRoleAttribute));
      case 'ai-code-sync':
        return Ok(aiCodeSync(_project, name, tags, roleAttribute, tagsRoleAttribute));
      default:
        break;
    }

    return Err(new ParserError(`Unknown project-scoped resource ${type}`));
  }

  private parseApplicationScopedResourceSpecifier(
    _application: ApplicationResource,
  ): Result<ResourceSpecifier, ParserError> {
    const parts = this.readResourceIdentifierParts();
    if (parts.err) {
      return parts;
    }

    const { type, name, tags, roleAttribute, tagsRoleAttribute } = parts.val;

    switch (type) {
      case 'version':
        return Ok(applicationVersion(_application, name, tags, roleAttribute, tagsRoleAttribute));
      default:
        break;
    }

    return Err(new ParserError(`Unknown application-scoped resource ${type}`));
  }

  private parseEnvironmentScopedResourceSpecifier(
    _environment: EnvironmentResource,
  ): Result<ResourceSpecifier, ParserError> {
    const parts = this.readResourceIdentifierParts();
    if (parts.err) {
      return parts;
    }

    const { type, name, tags, views, roleAttribute, tagsRoleAttribute, viewsRoleAttribute } = parts.val;

    switch (type) {
      case 'flag':
      case 'feature':
        return Ok(flag(_environment, name, tags, views, roleAttribute, tagsRoleAttribute, viewsRoleAttribute));
      case 'segment':
        return Ok(segment(_environment, name, tags, views, roleAttribute, tagsRoleAttribute, viewsRoleAttribute));
      case 'aiconfig':
        return Ok(aiConfig(_environment, name, tags, views, roleAttribute, tagsRoleAttribute, viewsRoleAttribute));
      case 'user':
        return Ok(user(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'holdout':
        return Ok(holdout(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'experiment':
        return Ok(experiment(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'notification-subscription':
        return Ok(notificationSubscription(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'environment-release-plan':
        return Ok(environmentReleasePlan(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'metric-data-source':
        return Ok(metricDataSource(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'assignment-data-source':
        return Ok(assignmentDataSource(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'attribute-data-source':
        return Ok(attributeDataSource(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'multi-armed-bandit':
        return Ok(multiArmedBandit(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'destination':
        return Ok(destination(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'product-analytics-dashboard':
        return Ok(productAnalyticsDashboard(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      case 'sdk-key':
        return Ok(sdkKey(_environment, name, tags, roleAttribute, tagsRoleAttribute));
      default:
        break;
    }

    return Err(new ParserError(`Unknown project and environment-scoped resource ${type}`));
  }

  private parseMemberScopedResourceSpecifier(_member: MemberResource): Result<ResourceSpecifier, ParserError> {
    const parts = this.readResourceIdentifierParts();
    if (parts.err) {
      return parts;
    }

    const { type, name, tags, roleAttribute, tagsRoleAttribute } = parts.val;

    switch (type) {
      case 'token':
        return Ok(token(_member, name, tags, roleAttribute, tagsRoleAttribute));
      default:
        break;
    }

    return Err(new ParserError(`Unknown member-scoped resource ${type}`));
  }

  private parseTrailingTokens(): Result<
    {
      tags: string[];
      views: string[];
      propertySelectors: PropertySelectors;
      tagsRoleAttribute?: string;
      viewsRoleAttribute?: string;
    },
    ParserError
  > {
    const tags: string[] = [];
    let tagsRoleAttribute: string | undefined;
    const views: string[] = [];
    let viewsRoleAttribute: string | undefined;
    let propertySelectors: PropertySelectors = {};
    let hasSeenView = false;

    while (
      [
        'tag',
        'view',
        'view-selector',
        'property-selector-start',
        'tags-role-attribute',
        'views-role-attribute',
      ].includes(this.currentToken.type)
    ) {
      switch (this.currentToken.type) {
        case 'tag':
          if (hasSeenView) {
            return Err(new ParserError('Tags must come before views', copy(this.currentToken)));
          }
          tags.push(this.currentToken.text);
          this.nextToken();
          break;
        case 'tags-role-attribute':
          tagsRoleAttribute = this.currentToken.text;
          this.nextToken();
          break;
        case 'views-role-attribute':
          viewsRoleAttribute = this.currentToken.text;
          this.nextToken();
          break;
        case 'view-selector':
          hasSeenView = true;
          this.nextToken(); // move to the view token
          break;
        case 'view':
          hasSeenView = true;
          views.push(this.currentToken.text);
          this.nextToken();
          break;
        case 'property-selector-start':
          const parts = this.parsePropertySelector(structuredClone(propertySelectors));
          if (parts.err) {
            return Err(parts.val);
          }
          propertySelectors = Object.assign(propertySelectors, parts.val.propertySelectors);
          this.nextToken();
          break;
        default:
          // This should never happen due to while condition check
          break;
      }
    }

    return Ok({ tags, views, propertySelectors, tagsRoleAttribute, viewsRoleAttribute });
  }

  private parsePropertySelector(
    propSelectors: PropertySelectors,
  ): Result<{ propertySelectors: PropertySelectors }, ParserError> {
    if (this.currentToken.type !== 'property-selector-start') {
      return Err(new ParserError('token is not of type property-selector-start'));
    }

    const validPropertyValue: () => boolean = () =>
      this.currentToken.type === 'property-selector-value' &&
      (this.currentToken.text === 'true' || this.currentToken.text === 'false');

    const setPropertyValue: () => void = () => {
      if (propSelectors.critical !== undefined) {
        const c = (propSelectors.critical = Boolean(this.currentToken.text === 'true'));
        propSelectors.critical = propSelectors.critical && c;
      } else {
        propSelectors.critical = Boolean(this.currentToken.text === 'true');
      }
    };

    const setAffectsSdkPayloadValue: () => void = () => {
      propSelectors.affectsSdkPayload = this.currentToken.text === 'true';
    };

    this.nextToken(); // go to property-selector-key
    switch (this.currentToken.text) {
      case 'critical':
        this.nextToken(); // go to property-selector-value
        if (validPropertyValue()) {
          setPropertyValue();
        } else {
          return Err(new ParserError("property-selector-value for 'critical' should be true or false"));
        }
        break;
      case 'affectsSdkPayload':
        this.nextToken(); // go to property-selector-value
        if (validPropertyValue()) {
          setAffectsSdkPayloadValue();
        } else {
          return Err(new ParserError("property-selector-value for 'affectsSdkPayload' should be true or false"));
        }
        break;
      default:
        return Err(new ParserError(`Unknown property selector: ${this.currentToken.text}`));
    }
    this.nextToken(); // go to property-selector-end
    return Ok({ propertySelectors: propSelectors });
  }

  private readResourceIdentifierParts(): Result<
    {
      type: ResourceType;
      name: string;
      tags: string[];
      views: string[];
      propertySelectors: PropertySelectors;
      roleAttribute?: string;
      tagsRoleAttribute?: string;
      viewsRoleAttribute?: string;
    },
    ParserError
  > {
    const type = this.currentToken.text;

    if (!isResourceType(type)) {
      return Err(new ParserError(`Expected valid resource type but got "${type}"`, copy(this.peekToken)));
    }

    if (!this.expectPeek('resource-identifier')) {
      return Err(
        new ParserError(
          `Expected next token to be "resource-identifier" but got "${this.peekToken.type}"`,
          copy(this.peekToken),
        ),
      );
    }

    const name = this.currentToken.text;

    this.nextToken();

    const trailingTokens = this.parseTrailingTokens();
    if (trailingTokens.err) {
      return Err(trailingTokens.val);
    }

    const { tags, views, propertySelectors, tagsRoleAttribute, viewsRoleAttribute } = trailingTokens.val;
    const roleAttribute = this.parseRoleAttributeFromName(name);

    // Supports both explicit tags and tagsRoleAttribute
    let _tags = tags;
    if (tagsRoleAttribute) {
      _tags = tags.filter((tag) => !tag.match(ROLE_ATTRIBUTE_REGEX));
    }

    // support both explicit views and viewsRoleAttribute
    let _views = views;
    if (viewsRoleAttribute) {
      _views = views.filter((v) => !v.match(ROLE_ATTRIBUTE_REGEX));
    }

    return Ok({
      type,
      name,
      tags: _tags,
      views: _views,
      propertySelectors,
      roleAttribute,
      tagsRoleAttribute,
      viewsRoleAttribute,
    });
  }

  private parseRoleAttributeFromName(name: string): string | undefined {
    const match = name.match(ROLE_ATTRIBUTE_REGEX);
    if (match) {
      return match[1];
    }

    return;
  }

  private expectPeek(tokenType: Token['type']): boolean {
    if (this.peekToken.type === tokenType) {
      this.nextToken();
      return true;
    }

    return false;
  }

  private isScopedResource() {
    if (this.currentToken.type !== 'scope') {
      return false;
    }

    this.nextToken();

    return true;
  }

  private nextToken() {
    this.currentToken = this.peekToken;
    this.peekToken = this.lexer.nextToken();
  }
}

export function parse(input: string): Result<ResourceSpecifier, ParserError> {
  const lexer = new Lexer(input);
  const parser = new Parser(lexer);
  const ast = parser.parse();
  return ast;
}

/**
 * We make a copy of the current token when saving errors
 * so that we save the token's value from that point in time.
 * (Otherwise, error tokens would all end up point to whatever
 * the current token was.)
 * @param obj
 * @returns a copy of the object
 */
function copy<T>(obj: T): T {
  return structuredClone(obj);
}
